// CO-780 follow-through — ONE table that turns a machine-readable `Error.Code` into a locale key.
//
// ── WHY THIS FILE EXISTS, AND WHY IT IS NOT AN `errors` NAMESPACE ─────────────────────────────────
// `utils/apiErrors.ts` has always carried an `errors:<CODE>` seam as step 1 of its fallback order,
// and there has never been an `errors` namespace to feed it. The obvious fix — create
// `public/locales/{en,ar,fr}/errors.json` and register `errors` in `src/i18n.js` — was rejected on
// four measured grounds (2026-09-21):
//
//  1. THE STRINGS ALREADY EXIST, ONCE. All seven assignment refusals are already keyed sentences in
//     `tournament.json` in en/ar/fr (26aab1dd7, repaired dafb61f7f/9dde24f33). An `errors.json`
//     would hold a SECOND copy of the same 7 sentences × 3 languages. Two homes for one sentence is
//     how ar/fr drift starts, and this stack gives no signal when they diverge.
//  2. MOBILE CANNOT SEE A NEW NAMESPACE. `mowazaf-flutter` fetches a FIXED list of namespace files
//     out of this repo (`['tournament','translation']`, merged last-wins) and has no namespaces of
//     its own. A key in `errors.json` is unreachable from the phone, so web and mobile would refuse
//     the same action with two different sentences. Keys in `tournament.json` serve both clients.
//  3. `tournament` IS ALREADY PRELOADED. It is in `i18n.js`'s `ns[]`, so every user already has the
//     bundle before the first toast can fire. A new namespace is one more blocking fetch per
//     language for every page load, to serve seven situations.
//  4. REGISTERING `errors` WOULD ARM A WILDCARD OVER EVERY ERROR IN THE APP. `i18n.js` sets
//     `fallbackNS: ['common','translation','tournament']`, and — MEASURED against the real bundles
//     with i18next 22.5.1, not assumed — fallbackNS resolves a lookup whose PRIMARY namespace does
//     not exist at all: `t('errors:Expired')` already returns "منتهي الصلاحية" (the `common` word)
//     on an Arabic page today. `Expired`, `Pending`, `Active`, `Required`, `Success`, `Duplicate`
//     and `Error` are all live keys in `common`/`translation`/`tournament`. Promoting that seam to
//     the primary path would mean the first server code that collides with a UI word renders that
//     ONE WORD as an entire refusal message. Per-call `fallbackNS: []` / `false` does NOT suppress
//     it (probed; i18next ignores the override), so there is no cheap guard.
//
// So: an EXPLICIT, enumerated table, fully-qualified with its namespace, consulted BEFORE the
// `errors:` seam precisely so an accidental fallbackNS hit can never beat a deliberate mapping.
//
// ── ADDING A CODE ────────────────────────────────────────────────────────────────────────────────
// A new server code needs BOTH halves in the same change:
//   1. an entry here, `CODE: 'tournament:<the literal English sentence>'`;
//   2. that sentence as a key in `public/locales/{en,ar,fr}/tournament.json` — ALL THREE. `i18n.js`
//      sets `fallbackLng: 'en'`, so a missing ar/fr does not render raw, it renders ENGLISH to that
//      user, which no QA pass in any language will ever show you. `scripts/i18n-audit.mjs` mode 2
//      is what counts that gap, but it cannot see these keys: they are reached through `t(variable)`
//      and the audit only extracts `t('literal')`. This comment is the guard rail.
// ⚠️ NEVER let a key contain ':' — `nsSeparator` is ':' so everything before the FIRST one is read
//    as the namespace. The `tournament:` prefix here is the one intentional separator per entry.
//
// Nothing in this table is tournament-specific by construction; it is the app-wide registry. Any
// module may add its codes, pointing at whichever namespace already owns the copy.

/** Server `Error.Code` → a fully-qualified i18next key (`<namespace>:<key>`). */
export const SERVER_ERROR_CODE_KEYS: Record<string, string> = {
  // WpsService and WpsHoldService refusals (v1.0.75, UAE). The copy lives in common.json, beside the WPS screen's.
  wpsNoPayroll: 'common:payroll.wps.uae.err.noPayroll',
  wpsEveryoneInFile: 'common:payroll.wps.uae.err.everyoneInFile',
  wpsPreflightFailed: 'common:payroll.wps.uae.err.preflightFailed',
  fxRateMissing: 'common:payroll.wps.uae.err.fxRateMissing',
  employeeBankRoutingMissing: 'common:payroll.wps.uae.err.bankRoutingMissing',
  wpsMonthBusy: 'common:payroll.wps.uae.err.monthBusy',
  wpsFileVoided: 'common:payroll.wps.uae.err.fileVoided',
  wpsLegacyFile: 'common:payroll.wps.uae.err.legacyFile',
  wpsFileNotStored: 'common:payroll.wps.uae.err.fileNotStored',
  wpsReasonRequired: 'common:payroll.wps.uae.err.reasonRequired',
  wpsNoteRequired: 'common:payroll.wps.uae.err.noteRequired',
  wpsNoteTooLong: 'common:payroll.wps.uae.err.noteTooLong',
  wpsLineAlreadyReleased: 'common:payroll.wps.uae.err.lineAlreadyReleased',
  wpsHoldInFile: 'common:payroll.wps.uae.err.holdInFile',
  wpsHoldBackOff: 'common:payroll.wps.uae.err.holdBackOff',
  wpsPaidOutsideOff: 'common:payroll.wps.uae.err.paidOutsideOff',
  wpsPeriodInvalid: 'common:payroll.wps.uae.err.periodInvalid',
  wpsPeriodOverlap: 'common:payroll.wps.uae.err.periodOverlap',
  wpsSettingInvalid: 'common:payroll.wps.uae.err.settingInvalid',
  wpsMarkPaidNeedsConfirm: 'common:payroll.wps.uae.err.markPaidNeedsConfirm',
  wpsMarkPaidHeldRefused: 'common:payroll.wps.uae.err.markPaidHeldRefused',
  wpsRegenerateInFile: 'common:payroll.wps.uae.err.regenerateInFile',
  wpsRegenerateInFileRefused: 'common:payroll.wps.uae.err.regenerateInFileRefused',
  // RefereeService.AcceptRefusalCodes — the accept path (CO-762/CO-780 widened it to Completed and
  // UnderRevision; these five are the cutoff it stops at).
  ASSIGNMENT_ALREADY_RESPONDED: 'tournament:You have already responded to this assignment.',
  ASSIGNMENT_MATCH_ARCHIVED:
    'tournament:This match was removed, so the assignment can no longer be accepted. Ask HR to record it for you if you officiated it.',
  ASSIGNMENT_MATCH_CANCELLED: 'tournament:This match was cancelled, so there is nothing left to accept.',
  ASSIGNMENT_MATCH_CLOSED:
    'tournament:This match is closed and its record is final. Ask HR to record your assignment for you if you officiated it.',
  ASSIGNMENT_PAYROLL_LOCKED:
    'tournament:This assignment is already on a payroll entry, so its record is final. Ask HR if something about it is wrong.',
  // RefereeService.DeclineRefusalCodes — decline is still Scheduled-only and pre-kickoff, deliberately
  // (it archives the row and voids its pay). Only the REASON became readable, not the rule.
  ASSIGNMENT_DECLINE_MATCH_NOT_SCHEDULED:
    'tournament:This match has already started or finished, so it can no longer be declined. Contact HR to be replaced.',
  ASSIGNMENT_DECLINE_MATCH_STARTED:
    'tournament:This match has already kicked off, so it can no longer be declined. Contact HR to be replaced.',
};

/**
 * The localized sentence for a server error code, or `''` when this build does not know the code.
 *
 * `''` — never the code, never the key — is the contract: the caller falls through to the server's
 * own English description, which is worse copy but is still a sentence a human can read. A raw
 * `ASSIGNMENT_MATCH_CLOSED` on screen would be strictly worse than the untranslated prose it
 * replaced, so this function is written to make that outcome unreachable.
 *
 * `t` may be bound to ANY namespace — every entry carries its own, so the caller's `defaultNS` is
 * irrelevant.
 */
export function localizeServerErrorCode(
  code: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (!code) return '';
  const key = SERVER_ERROR_CODE_KEYS[code];
  if (!key) return '';
  // `defaultValue: ''` makes a missing key return empty instead of echoing itself, so a locale gap
  // degrades to the server's sentence rather than painting an English key at an Arabic reader.
  const value = t(key, { defaultValue: '' });
  if (!value || value === key) return '';
  return value;
}

// ── 2026-09-28: DESCRIPTION-keyed refusals (shift swaps) ─────────────────────────────────────────
// Some server refusals carry a generic `Error.Code` (`BadRequest`, `NotFound`, `PermissionDenied`) and a
// FIXED English sentence as the description. The mobile app already renders those through
// `AppLocalizations.of(context, description)`, i.e. the sentence itself is the translation.json key, so the
// fix that reaches both clients is to key the sentence, not to invent a code. This set is the web half: an
// EXPLICIT allowlist (never "try every description as a key", which would re-arm the fallbackNS wildcard
// described in this file's header). Each entry is a key in `public/locales/{en,ar,fr}/translation.json`
// and a literal description in Mowazaf.Server/Services/Attendance/ShiftSwapService.cs. Keep all three in
// step. ⚠️ No entry may contain ':' (nsSeparator).
export const SERVER_ERROR_DESCRIPTION_KEYS: ReadonlySet<string> = new Set<string>([
  'Shift swap is not enabled for you',
  'Counterparty is required',
  'You cannot swap a shift with yourself',
  'Counterparty employee not found',
  'This colleague is not able to participate in shift swaps',
  'Your selected shift was not found',
  'The selected colleague shift was not found',
  'No manager is available to approve a swap between you and this colleague. Please contact HR.',
  'This shift is already part of an active swap request',
  "The colleague's shift is already part of an active swap request",
  'Swap request not found',
  'This request is no longer awaiting your response',
  'This request was just updated. Please refresh and try again.',
  'This request is not awaiting approval',
  "The requester's shift is no longer available",
  "The colleague's shift is no longer available",
  'A shift in this swap has already been worked',
  'A shift in this swap has already started',
  'The colleague already has a shift overlapping that time',
  'The requester already has a shift overlapping that time',
  'The colleague is on approved leave on that day',
  'The requester is on approved leave on that day',
  'The swap could not be applied because the schedule changed. Please refresh and try again.',
  'This request cannot be rejected',
  'Only a pending swap can be cancelled',
  'This shift has already started and can no longer be swapped.',
  'One of the shifts in this swap has already started and can no longer be swapped.',
  // Match shifts are refused in swaps (owner decision 2026-10-14): ShiftSwapService, at request, accept,
  // approve and apply.
  'Match assignments cannot be swapped. If you cannot attend, ask HR to replace you.',
  'You can only swap a future shift (your shift).',
  "You can only swap a future shift (the colleague's shift).",
  // CO-930: RecurringLeaveService (preview + submit) and EncashmentService answer this when the person has
  // no live (non-archived) balance row for the leave type.
  'Leave balance not found',
]);

/**
 * The localized sentence for an allowlisted server description, or `''` when the description is not in
 * {@link SERVER_ERROR_DESCRIPTION_KEYS} or this bundle has no value for it (the caller then shows the
 * server's English, exactly as before).
 */
export function localizeServerErrorDescription(
  description: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const d = description?.trim();
  if (!d || !SERVER_ERROR_DESCRIPTION_KEYS.has(d)) return '';
  const key = `translation:${d}`;
  const value = t(key, { defaultValue: '' });
  if (!value || value === key) return '';
  return value;
}
